import React from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import capitalize from 'lodash/capitalize';
import pt from 'prop-types';

import { hasPermission, isValueEqual, classNames } from 'utils';
import { openFrontendAccessUrl } from 'utils/app-frontend';
import { openWebUrl } from 'utils/web-url';

import Link from 'components/link';
import Icon from 'components/icon';
import AppIcon from 'components/apps/icon';
import { FadeIn } from 'components/transitions';

import './nav.scss';

const AUTO_TOGGLED = new Set();

export default class NavItem extends React.PureComponent {
  static propTypes = {
    path: pt.oneOfType([pt.string, pt.number]),
    link: pt.string,
    // The link is the app's frontend
    frontend: pt.bool,
    icon: pt.string,
    label: pt.string,
    items: pt.array,
    target: pt.string,
    app_id: pt.string,
    faIcon: pt.string,
    isActive: pt.bool,
    location: pt.object,
    otherItems: pt.array,
    permission: pt.string,
    activeGroups: pt.object,
    activeChildItem: pt.object,

    toggleNavGroup: pt.func,
    onClickNavGroup: pt.func,
  };

  static contextTypes = {
    client: pt.object,
    isAdvancedUserPermissions: pt.bool,
    user: pt.object.isRequired,
    router: pt.object,
    setTitle: pt.func.isRequired,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      childItems: this.getChildItems(props.items),
    };
  }

  componentDidUpdate(prevProps) {
    if (!isValueEqual(this.props.items, prevProps.items)) {
      this.setState({ childItems: this.getChildItems(this.props.items) });
    }
  }

  getChildItems(items) {
    const { isAdvancedUserPermissions = false, user } = this.context;
    let childItems = items ? items.filter(Boolean) : [];
    if (childItems.length > 0) {
      childItems = childItems.filter(
        (item) =>
          !item.permission ||
          hasPermission(user, item.permission, item.link, {
            isAdvancedUserPermissions,
          }),
      );
    }
    return childItems;
  }

  onClickNavLink = (event) => {
    event.preventDefault();
    const { toggleNavGroup, target, label } = this.props;
    if (toggleNavGroup) {
      if (event.target.dataset.open === undefined) {
        event.preventDefault();
        return;
      }
      if (event.target.dataset.open === 'false') {
        toggleNavGroup(label);
      }
    }
    let { link } = this.props;
    if (link === undefined) {
      ({ link } = this.state.childItems[0]);
    }
    if (link) {
      if (target && target !== '_self') {
        const { frontend, app_id: appId } = this.props;
        // An app's frontend opens signed in, as from the app's own pages
        if (frontend && appId) {
          openFrontendAccessUrl(appId, link);
        } else {
          openWebUrl(link, target);
        }
      } else {
        this.context.router.push(link);
        this.resetScrollTop();
      }
    }
  };

  resetScrollTop() {
    const main = document.getElementById('main');
    if (main) {
      main.scrollTop = 0;
    }
  }

  renderSortableList(navItemChildren, label) {
    return (
      <Droppable droppableId={label} type="item">
        {(dropProvided) => (
          <ul ref={dropProvided.innerRef} className="nav-group-list">
            {navItemChildren.map((item, index) => (
              <Draggable key={item.key} draggableId={item.label} index={index}>
                {(dragProvided) => (
                  <li
                    ref={dragProvided.innerRef}
                    className="open"
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                  >
                    <NavItem {...item} />
                  </li>
                )}
              </Draggable>
            ))}

            {dropProvided.placeholder}
          </ul>
        )}
      </Droppable>
    );
  }

  renderLabel() {
    const { label } = this.props;
    return label;
  }

  onOpenKebab = (kebabOpen) => {
    this.setState({ kebabOpen });
  };

  renderItem(childItems) {
    const {
      label,
      icon,
      faIcon,
      app_id,
      isActive,
      onClickNavGroup,
      activeGroups,
      otherItems,
    } = this.props;

    const { client } = this.context;

    const isActiveGroup = Boolean(activeGroups && activeGroups[label]);
    const app = client?.appsById?.[app_id];
    const showAppIcon = app && otherItems.some((i) => i.label === label);

    return (
      <>
        {faIcon ? (
          <span className="icon-id">
            <Icon fa={faIcon} faType="regular" />
          </span>
        ) : (
          icon && (
            <span className="icon-id">
              <Icon type={`${isActive ? `${icon}-active` : icon}`} />
            </span>
          )
        )}

        {this.renderLabel()}

        {app && (
          <span className={classNames('icon-app', { show: showAppIcon })}>
            <AppIcon image={app.logo_icon} name={app.name} size={20} />
          </span>
        )}

        {childItems.length > 0 && (
          <span onClick={onClickNavGroup} className="chevron" data-id={label}>
            <Icon
              fa="chevron-down icon"
              className={isActiveGroup ? 'open' : ''}
            />
          </span>
        )}
      </>
    );
  }

  renderInner() {
    const {
      label,
      link,
      location,
      activeChildItem,
      onClickNavGroup,
      activeGroups,
      path,
    } = this.props;

    const { childItems } = this.state;
    const isActiveGroup = Boolean(activeGroups && activeGroups[label]);

    return (
      <>
        {link ? (
          <Link
            to={link}
            onClick={this.onClickNavLink}
            data-open={isActiveGroup}
          >
            {this.renderItem(childItems)}
          </Link>
        ) : (
          <a
            href=""
            data-id={label}
            data-open={isActiveGroup}
            onClick={link === false ? onClickNavGroup : this.onClickNavLink}
          >
            {this.renderItem(childItems)}
          </a>
        )}

        {childItems.length > 0 && isActiveGroup && (
          <FadeIn
            component="ul"
            className="nav-group-list"
            transitionAppear={true}
          >
            {childItems.map((item, index) => {
              return (
                <NavItem
                  key={`${index}.${item.link}`}
                  index={index}
                  path={`${path}.items.${index}`}
                  isActive={activeChildItem?.link === item?.link}
                  location={location}
                  activeChildItem={activeChildItem}
                  otherItems={childItems.filter((i) => i !== item)}
                  {...item}
                />
              );
            })}
          </FadeIn>
        )}
      </>
    );
  }

  render() {
    const {
      label,
      link,
      items,
      isActive,
      toggleNavGroup,
      activeGroups,
      permission,
    } = this.props;

    const { childItems } = this.state;
    const { isAdvancedUserPermissions = false, user } = this.context;

    if (!label) {
      return null;
    }

    if (items && items.length > 0 && childItems.length === 0) {
      return null;
    }

    if (!hasPermission(user, permission, link, { isAdvancedUserPermissions })) {
      return null;
    }

    const isActiveGroup = Boolean(activeGroups && activeGroups[label]);

    if (isActive) {
      if (!AUTO_TOGGLED.has(label) && !isActiveGroup && toggleNavGroup) {
        AUTO_TOGGLED.add(label);

        setTimeout(() => {
          toggleNavGroup(label);
        }, 1);
      }

      this.context.setTitle(
        label.startsWith('All ')
          ? capitalize(label.replace('All ', ''))
          : label,
      );
    }

    return (
      <li className={classNames({ active: isActive, open: isActiveGroup })}>
        {this.renderInner()}
      </li>
    );
  }
}
